// Phase 26E.1 — Admin Order Grid.
// ERP-style order list with checkbox multi-select + bulk Accept / Reject /
// Fulfill / Edit / Print. Server-side pagination + sorting + filters.
// Reuses the shared BulkActionGrid component so 26E.2 (Claim Grid) and 26F
// (Retailer Grid) can pick it up without changes.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, X, PackageCheck, Pencil, Printer,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import BulkActionGrid from "@/components/BulkActionGrid";

const TESTID = "admin-order-grid";

const STATUS_STYLES = {
  placed:            "bg-blue-100 text-blue-700",
  booked:            "bg-blue-100 text-blue-700",
  submitted:         "bg-blue-100 text-blue-700",
  accepted:          "bg-emerald-100 text-emerald-700",
  approved:          "bg-emerald-100 text-emerald-700",
  partial_fulfilled: "bg-amber-100 text-amber-700",
  fulfilled:         "bg-purple-100 text-purple-700",
  delivered:         "bg-purple-100 text-purple-700",
  rejected:          "bg-red-100 text-red-700",
  cancelled:         "bg-neutral-200 text-neutral-700",
};
const STATUS_LABELS = {
  placed: "Placed", booked: "Placed", submitted: "Placed",
  accepted: "Accepted", approved: "Accepted",
  partial_fulfilled: "Partial Fulfilled",
  fulfilled: "Fulfilled", delivered: "Fulfilled",
  rejected: "Rejected", cancelled: "Cancelled",
};

const ACCEPTABLE = new Set(["placed", "booked", "submitted"]);
const TERMINAL = new Set(["fulfilled", "rejected", "cancelled", "delivered"]);
const EDITABLE = new Set(["placed", "booked", "submitted", "accepted", "approved"]);
const FULFILLABLE = new Set(["accepted", "approved", "partial_fulfilled", "dispatched"]);

const isPrimaryOrder = (r) => r.order_channel === "primary_to_company";
// Primary orders: any role (including ASM) may accept a "placed" one —
// that's the intended acceptor for this channel. Secondary orders: ASM
// stays excluded, same restriction as before this row-level split existed.
const isPrimaryAcceptable = (r, isASM) =>
  isPrimaryOrder(r) ? ACCEPTABLE.has(r.status) : (!isASM && ACCEPTABLE.has(r.status));

export default function AdminOrderGrid() {
  const nav = useNavigate();
  const { user } = useAuth();
  // ASM gets a narrower grid: only Edit / Reject, and only while the order
  // hasn't entered fulfillment yet (same eligibility as Edit) — ASM
  // shouldn't be accepting or fulfilling orders themselves, and shouldn't
  // be able to reject/edit one that's already fulfilled/dispatched.
  const isASM = user?.role_code === "ASM";
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/zones").catch(() => ({ data: { items: [] } })),
      api.get("/sub-zones").catch(() => ({ data: { items: [] } })),
      api.get("/distributors", { params: { page_size: 2000 } }).catch(() => ({ data: { items: [] } })),
      api.get("/users", { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
    ]).then(([z, sz, d, u]) => {
      setZones(z.data.items || []);
      setSubZones(sz.data.items || []);
      setDistributors(d.data.items || []);
      setUsers(u.data.items || []);
    });
  }, []);

  const filters = useMemo(() => ([
    {
      key: "statuses", type: "checkbox_group", label: "Order State",
      // Each option's value is every raw DB status the "State" column
      // displays under that same badge label (see STATUS_LABELS above) —
      // not just one of them. An order auto-approved as a secondary order
      // is stored as status="approved" (never "accepted"), but the State
      // column shows it as "Accepted" — so the "Accepted" filter chip has
      // to match "approved" too, or it silently returns nothing for the
      // vast majority of orders that actually show that badge. Same idea
      // for "Placed" (placed/booked/submitted) and "Fulfilled"
      // (fulfilled/delivered).
      options: [
        { value: "placed,booked,submitted",   label: "Placed" },
        { value: "accepted,approved",         label: "Accepted" },
        { value: "partial_fulfilled",         label: "Partial" },
        { value: "fulfilled,delivered",       label: "Fulfilled" },
        { value: "rejected",                  label: "Rejected" },
        { value: "cancelled",                 label: "Cancelled" },
      ],
    },
    { key: "date_range", type: "date_range", label: "Order Date" },
    { key: "zone_ids", type: "single_select", label: "Zone",
      options: zones.map(z => ({ value: z.id, label: z.code })) },
    { key: "sub_zone_ids", type: "single_select", label: "Sub-Zone",
      options: subZones.map(z => ({ value: z.id, label: z.code || z.name })) },
    { key: "distributor_ids", type: "single_select", label: "Distributor",
      options: distributors.map(d => ({ value: d.id, label: d.name })) },
    { key: "order_type", type: "single_select", label: "Type",
      options: [
        { value: "secondary", label: "Secondary" },
        { value: "primary",   label: "Primary" },
      ] },
    { key: "user_ids", type: "single_select", label: "User",
      options: users.map(u => ({ value: u.id, label: u.name })) },
    { key: "order_no", type: "text", label: "Order #", placeholder: "ORD-…" },
  ]), [zones, subZones, distributors, users]);

  const columns = useMemo(() => ([
    { key: "order_no", label: "Order ID", sortable: true, width: 130,
      render: (row) => (
        <span className="font-mono text-neutral-900 font-semibold">{row.order_no}</span>
      ),
    },
    { key: "net_amount", label: "Amount ₹", sortable: true, align: "right", width: 100,
      render: (row) => `₹${Number(row.net_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`,
    },
    { key: "order_date", label: "Order Date", sortable: true, width: 105 },
    { key: "expected_delivery_date", label: "Expected", width: 105,
      render: (r) => r.expected_delivery_date || "—" },
    { key: "fulfilled_at", label: "Fulfilled", width: 105,
      render: (r) => r.fulfilled_at ? String(r.fulfilled_at).slice(0, 10) : "—" },
    { key: "status", label: "State", sortable: true, width: 120,
      render: (row) => (
        <span data-testid={`${TESTID}-status-${row.id}`}
              className={"inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider " + (STATUS_STYLES[row.status] || "bg-neutral-200 text-neutral-700")}>
          {STATUS_LABELS[row.status] || row.status}
        </span>
      ),
    },
    { key: "beat_name", label: "Beat", width: 130, render: (r) => r.beat_name || "—" },
    { key: "user_name", label: "User", width: 130, render: (r) => r.user_name || "—" },
    { key: "retailer_name", label: "Outlet", width: 150,
      render: (r) => (
        <div>
          <div className="text-neutral-900 truncate">{r.retailer_name || "—"}</div>
          {r.retailer_code && <div className="text-[10px] text-neutral-400 font-mono">{r.retailer_code}</div>}
        </div>
      ) },
    { key: "outlet_type", label: "Outlet Type", width: 110,
      render: (r) => r.outlet_type || "—" },
    { key: "distributor_name", label: "Distributor", width: 150,
      render: (r) => r.distributor_name || "—" },
    // Phase 26D.3 KG-primary extensions
    { key: "total_qty_kg", label: "Qty (kg)", sortable: false, align: "right", width: 80,
      render: (r) => (r.total_qty_kg != null
        ? Number(r.total_qty_kg).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })
        : "—") },
    { key: "total_qty_pcs", label: "Qty (pcs)", sortable: false, align: "right", width: 75,
      render: (r) => r.total_qty_pcs != null ? r.total_qty_pcs.toLocaleString("en-IN") : "—" },
    { key: "entered_uom_chips", label: "Entered As", width: 150,
      render: (r) => (
        <div className="flex gap-1 flex-wrap">
          {(r.entered_uom_chips || []).map((c, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700 font-mono">
              {c}
            </span>
          ))}
        </div>
      ) },
  ]), []);

  const onLoad = async (query) => {
    const params = { ...query, enrich: "grid" };
    // Flatten date_range → date_from / date_to.
    if (params.date_range) {
      Object.assign(params, params.date_range);
      delete params.date_range;
    }
    // Filter out empty-string values so backend doesn't try to $in an empty list.
    Object.keys(params).forEach(k => { if (params[k] === "" || params[k] == null) delete params[k]; });
    // multi-value: array → comma-joined
    ["statuses", "zone_ids", "sub_zone_ids", "distributor_ids", "user_ids", "beat_ids"].forEach(k => {
      if (Array.isArray(params[k])) params[k] = params[k].join(",");
    });
    const { data } = await api.get("/orders", { params });
    return { rows: data.items || [], total: data.total || 0, page_size: data.page_size };
  };

  const bulkActions = useMemo(() => ([
    {
      key: "accept",
      label: "Accept",
      icon: Check,
      // A primary (distributor→company) order has its own dedicated
      // Placed→Accepted workflow (POST /primary-orders/{id}/accept) — same
      // mapped-ASM/manager authorization as the field-app Primary Order
      // page, distinct from the generic secondary-order bulk-accept below.
      // ASM is deliberately excluded from accepting SECONDARY orders (that
      // restriction is unrelated and still applies), but they ARE the
      // intended acceptor for primary ones, so this can't be a blanket
      // per-role toggle — eligibility has to be decided per row.
      appliesTo: (rows) => rows.some(r => isPrimaryAcceptable(r, isASM)),
      count: (rows) => rows.filter(r => isPrimaryAcceptable(r, isASM)).length,
      onExecute: async (rows) => {
        const eligible = rows.filter(r => isPrimaryAcceptable(r, isASM));
        const primaryRows = eligible.filter(isPrimaryOrder);
        const secondaryRows = eligible.filter(r => !isPrimaryOrder(r));

        let applied = 0, skipped = 0;
        if (primaryRows.length) {
          const results = await Promise.allSettled(
            primaryRows.map(r => api.post(`/primary-orders/${r.id}/accept`))
          );
          results.forEach(res => { res.status === "fulfilled" ? applied++ : skipped++; });
        }
        if (secondaryRows.length) {
          const { data } = await api.post("/orders/bulk/accept",
            { order_ids: secondaryRows.map(r => r.id) });
          applied += data.applied.length;
          skipped += data.skipped.length;
        }
        return { toast: `Accepted ${applied} · Skipped ${skipped}` };
      },
    },
    {
      key: "reject",
      label: "Reject",
      icon: X,
      destructive: true,
      // ASM: same eligibility as Edit — stops once fulfillment has started
      // (partial_fulfilled/dispatched/fulfilled/delivered), not just at the
      // final terminal states.
      appliesTo: (rows) => rows.some(r => isASM ? EDITABLE.has(r.status) : !TERMINAL.has(r.status)),
      count: (rows) => rows.filter(r => isASM ? EDITABLE.has(r.status) : !TERMINAL.has(r.status)).length,
      requiresPrompt: true,
      promptDescription: "Enter a rejection reason. It will be recorded on every selected order's status log.",
      promptSchema: [{ key: "reason", label: "Rejection Reason", type: "textarea",
                        required: true, minLength: 3,
                        placeholder: "e.g. Distributor stock unavailable" }],
      confirmLabel: "Reject selected",
      onExecute: async (rows, values) => {
        const eligible = rows.filter(r => isASM ? EDITABLE.has(r.status) : !TERMINAL.has(r.status)).map(r => r.id);
        const { data } = await api.post("/orders/bulk/reject",
          { order_ids: eligible, reason: values.reason.trim() });
        return { toast: `Rejected ${data.applied.length} · Skipped ${data.skipped.length}` };
      },
    },
    {
      key: "fulfill",
      label: "Fulfill",
      icon: PackageCheck,
      appliesTo: (rows) => rows.length === 1 && FULFILLABLE.has(rows[0].status),
      onExecute: async (rows) => {
        nav(`/orders/${rows[0].id}`);
        return { toast: "Opening fulfillment…" };
      },
    },
    {
      key: "edit",
      label: "Edit",
      icon: Pencil,
      appliesTo: (rows) => rows.length === 1 && EDITABLE.has(rows[0].status),
      onExecute: async (rows) => {
        nav(`/orders/${rows[0].id}`);
        return { toast: "Opening order…" };
      },
    },
    {
      key: "print",
      label: "Print",
      icon: Printer,
      appliesTo: (rows) => rows.length > 0,
      onExecute: async (rows) => {
        const ids = rows.map(r => r.id).join(",");
        const { data } = await api.get(
          `/orders/bulk/print?ids=${encodeURIComponent(ids)}`,
          { responseType: "blob" });
        const url = URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url; a.download = `orders-bulk-${rows.length}.pdf`; a.click();
        return { toast: `Downloaded PDF for ${rows.length} order(s)` };
      },
    },
  ]), [nav, isASM]);

  // ASM never gets Fulfill. Accept stays visible — its own appliesTo/
  // onExecute above already restricts ASM to primary orders only, so
  // there's nothing left to filter out at the action-list level.
  const visibleBulkActions = isASM
    ? bulkActions.filter((a) => a.key !== "fulfill")
    : bulkActions;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid={`${TESTID}-page`}>
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Orders" }]}
        title="Orders"
        subtitle="Review orders and accept, reject, fulfil, edit or print them — one at a time or many at once."
        favorite={{ key: "admin.orders", type: "route", label: "Admin Orders", route: "/admin/orders" }}
        testid={`${TESTID}-page-header`}
      />
      <BulkActionGrid
        testid={TESTID}
        filters={filters}
        columns={columns}
        onLoad={onLoad}
        bulkActions={visibleBulkActions}
        onRowClick={(row) => nav(`/orders/${row.id}`)}
        rowKey="id"
        initialSort={{ by: "booking_ts", dir: "desc" }}
      />
    </div>
  );
}
